/**
 * RedFox Added to Cart Popup Plugin
 *
 * @since 1.0.0
 */

import { popupManager } from './components';
import { handleCartAddItem } from './handlers/fetch-events';
import { initWooCommerceEventListeners } from './handlers/woocommerce-events';
import { handleCartDataFromFilter } from './services/cart-service';
import {
  destroyFetchInterceptor,
  initFetchInterceptor,
  onCartAddItem,
} from './utils/fetch-interceptor';
import type { AddedProductData, CartPopupData, RedFoxPopupGlobal } from './utils/global-interfaces';

/**
 * Initialize the popup system
 */
const initPopup = (): void => {
  // Check if we have cart data from the WooCommerce filter
  const config = window.redfoxAddedToCartPopup;
  if (config?.cartData?.products && config.cartData.products.length > 0) {
    handleCartDataFromFilter(config.cartData);
  }

  // Initialize fetch interceptor as fallback
  initFetchInterceptor();

  // Initialize WooCommerce event listeners
  initWooCommerceEventListeners();

  // Subscribe to cart add item events from fetch interceptor
  const unsubscribe = onCartAddItem(handleCartAddItem);

  // Initialize popup manager
  popupManager.init();

  // Create global popup object
  const redFoxPopup: RedFoxPopupGlobal = {
    showPopup: (title?: string, content?: string, className?: string) => {
      popupManager.show(title, content, className);
    },
    showCartPopup: (
      addedProduct: AddedProductData,
      cartData?: CartPopupData,
      className?: string
    ) => {
      popupManager.showCart(addedProduct, cartData, className);
    },
    hidePopup: () => {
      popupManager.hide();
    },
    initPopup: () => {
      popupManager.init();
    },
    destroyPopup: () => {
      popupManager.destroy();
      destroyFetchInterceptor();
      unsubscribe();
    },
  };

  // Expose to global window object
  window.RedFoxPopup = redFoxPopup;
};

/**
 * Initialize when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPopup);
} else {
  initPopup();
}

// Export for potential module usage
export { popupManager };
export type { RedFoxPopupGlobal };
